import {
  CheckCircle as CheckCircleIcon,
  TimerOutlined as TimerOutlinedIcon,
  WarningAmber as WarningAmberIcon,
} from '../icons';
import React from 'react';
import { Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';

/**
 * Resolve a subscription projection into display config:
 * label / short label / colors / icon. Shared by the dashboard badge and the
 * side-nav status row.
 */
export function subscriptionStatusConfig(subscription) {
  if (!subscription) return null;

  const inTrial = subscription.in_trial === true;
  const hasAccess = subscription.has_access === true;
  const status = subscription.status;
  const daysLeft = subscription.days_left ?? 0;
  const dayWord = daysLeft === 1 ? 'day' : 'days';

  if (inTrial) {
    return {
      label: `Trial · ${daysLeft} ${dayWord} left`,
      shortLabel: `Trial · ${daysLeft}d`,
      tooltip: `Trial · ${daysLeft} ${dayWord} left`,
      color: '#7c3aed',
      bg: 'rgba(124, 58, 237, 0.10)',
      Icon: TimerOutlinedIcon,
    };
  }
  if (status === 'active' && hasAccess) {
    return {
      label: `Subscription active · ${daysLeft} ${dayWord} left`,
      shortLabel: `Active · ${daysLeft}d`,
      tooltip: `Subscription active · ${daysLeft} ${dayWord} left`,
      color: '#059669',
      bg: 'rgba(5, 150, 105, 0.10)',
      Icon: CheckCircleIcon,
    };
  }
  return {
    label: 'Subscription inactive',
    shortLabel: 'Inactive',
    tooltip: 'Subscription inactive, tap to renew',
    color: '#dc2626',
    bg: 'rgba(220, 38, 38, 0.10)',
    Icon: WarningAmberIcon,
  };
}

/**
 * Compact subscription indicator: shows the current status + remaining days
 * and links to the billing page. Renders nothing until the subscription loads.
 */
export function SubscriptionStatusBadge({ sx = {} }) {
  const navigate = useNavigate();
  const { subscription, loading } = useSubscription();

  if (loading || !subscription) return null;

  const config = subscriptionStatusConfig(subscription);
  if (!config) return null;

  const { Icon } = config;

  return (
    <Chip
      icon={<Icon sx={{ color: `${config.color} !important`, fontSize: 18 }} />}
      label={config.label}
      onClick={() => navigate('/admin/billing')}
      sx={{
        fontWeight: 700,
        color: config.color,
        bgcolor: config.bg,
        border: `1px solid ${config.color}33`,
        cursor: 'pointer',
        '&:hover': { bgcolor: config.bg, filter: 'brightness(0.97)' },
        ...sx,
      }}
    />
  );
}

export default SubscriptionStatusBadge;
